import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  apiClient,
  CryptoAIBriefResponse,
  Core7BriefItem,
} from '../../api/client';
import {
  compareDecimalStrings,
  getDecimalSign,
  isDecimalString,
} from '../../shared/domain/decimal';
import { formatFinancialPrice } from '../../shared/domain/formatters';
import { CryptoSymbolCell } from '../../components/common';
import { CRYPTO_BILINGUAL, CARD_TITLES_BILINGUAL } from '../../shared/constants/bilingualTitles';

export interface CryptoAIBriefViewProps {
  colorMode?: 'mainland' | 'international';
}

// Stage 063 Extended Tactical Models (Strictly type-safe without mutating frozen client.ts)
export interface MacroTacticalStance {
  posture: string;
  posture_label: string;
  headline: string;
  bias_summary: string;
  risk_level: string;
  primary_driver: string;
  invalidation_trigger: string;
}

export interface CandidateOpportunity {
  symbol: string;
  setup_type: string;
  stance_label: string;
  entry_zone_low?: string | null;
  entry_zone_high?: string | null;
  invalidation_level?: string | null;
  target_zone_low?: string | null;
  target_zone_high?: string | null;
  risk_reward_ratio?: string | null;
  data_state?: string;
  thesis: string;
  drill_down_route: string;
  drill_down_label: string;
}

export interface FragilityRiskRadar {
  air_pocket_warning: string;
  squeeze_risk: string;
  basis_dislocation: string;
  drill_down_route: string;
  drill_down_label: string;
}

export interface FalsifiableHypothesis {
  hypothesis_id: string;
  statement: string;
  confidence_score?: string | null;
  falsification_condition: string;
  drill_down_route: string;
  drill_down_label: string;
}

export interface ExtendedCore7BriefItem extends Core7BriefItem {
  tactical_bias?: string | null;
  suggested_action_zone?: string | null;
  risk_reward_ratio?: string | null;
}

export interface ExtendedCryptoAIBriefResponse extends CryptoAIBriefResponse {
  core7_briefs: ExtendedCore7BriefItem[];
  macro_tactical_stance?: MacroTacticalStance | null;
  top_candidate_opportunities?: CandidateOpportunity[];
  fragility_risk_radar?: FragilityRiskRadar | null;
  falsifiable_hypotheses?: FalsifiableHypothesis[];
}


/**
 * Calculates visual percentage position for the Price-to-Fib range bar.
 * Uses exact BigInt decimal-string arithmetic with zero binary float conversions or operations.
 * Returns exact clamped integer percentage string (e.g. "0" ~ "100") or null.
 */
function getPriceToFibPercent(
  spotPriceStr: string | null | undefined,
  support382Str: string | null | undefined,
  resistance618Str: string | null | undefined
): string | null {
  if (!spotPriceStr || !support382Str || !resistance618Str) return null;
  if (
    !isDecimalString(spotPriceStr) ||
    !isDecimalString(support382Str) ||
    !isDecimalString(resistance618Str)
  ) {
    return null;
  }

  // Ensure support < resistance
  if (compareDecimalStrings(support382Str, resistance618Str) >= 0) {
    return null;
  }

  try {
    const sClean = spotPriceStr.trim();
    const suppClean = support382Str.trim();
    const resClean = resistance618Str.trim();

    const [sInt = '0', sFrac = ''] = sClean.split('.');
    const [suppInt = '0', suppFrac = ''] = suppClean.split('.');
    const [resInt = '0', resFrac = ''] = resClean.split('.');

    const scale = Math.max(sFrac.length, suppFrac.length, resFrac.length, 6);
    const scaleFactor = 10n ** BigInt(scale);

    const parseToBig = (intPart: string, fracPart: string): bigint => {
      const isNeg = intPart.startsWith('-');
      const absInt = isNeg ? intPart.slice(1) : intPart;
      const absVal = BigInt(absInt) * scaleFactor + BigInt(fracPart.padEnd(scale, '0'));
      return isNeg ? -absVal : absVal;
    };

    const spotBig = parseToBig(sInt, sFrac);
    const suppBig = parseToBig(suppInt, suppFrac);
    const resBig = parseToBig(resInt, resFrac);

    const rangeBig = resBig - suppBig;
    if (rangeBig <= 0n) return null;

    const diffBig = spotBig - suppBig;
    if (diffBig <= 0n) return '0';
    if (diffBig >= rangeBig) return '100';

    // Exact integer percentage with half-up rounding using pure BigInt math:
    const pctBig = (diffBig * 100n + (rangeBig / 2n)) / rangeBig;
    const clampedBig = pctBig < 0n ? 0n : pctBig > 100n ? 100n : pctBig;
    return clampedBig.toString();
  } catch {
    return null;
  }
}

import { VisibilityRequestCoordinator } from '../regime/TrendWeatherGrid';

export const CryptoAIBriefView: React.FC<CryptoAIBriefViewProps> = ({
  colorMode = 'mainland',
}) => {
  const [data, setData] = useState<ExtendedCryptoAIBriefResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
  const coordinatorRef = useRef<VisibilityRequestCoordinator<ExtendedCryptoAIBriefResponse>>(new VisibilityRequestCoordinator());

  const fetchBrief = useCallback(async (bypassCache = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await coordinatorRef.current.execute(
        (signal) => apiClient.getCryptoBrief({ signal }) as Promise<ExtendedCryptoAIBriefResponse>,
        bypassCache,
      );
      if (!res) return;
      setData(res);
    } catch (err: any) {
      if (!document.hidden) {
        console.error('Failed to fetch Crypto AI Brief:', err);
        setError(err?.message || '网络连接或鉴权失败，无法获取加密决策简报');
      }
    } finally {
      if (!coordinatorRef.current.hasActivePromise()) {
        setLoading(false);
      }
    }
  }, []);

  // Stage 066: 60-second cadence + Page Visibility pause/resume
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    let isSubscribed = true;

    const startTimer = () => {
      if (timer) clearInterval(timer);
      timer = setInterval(() => {
        if (!document.hidden && isSubscribed) {
          fetchBrief();
        }
      }, 60000);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        coordinatorRef.current.onHide();
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      } else {
        if (isSubscribed) {
          coordinatorRef.current.onResume();
          fetchBrief();
          startTimer();
        }
      }
    };

    fetchBrief();
    if (!document.hidden) {
      startTimer();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      isSubscribed = false;
      coordinatorRef.current.onHide();
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchBrief]);

  // Stage 066: 5-second cooldown timer for manual refresh
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const cdTimer = setInterval(() => {
      setCooldownRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(cdTimer);
  }, [cooldownRemaining]);

  const handleManualRefresh = useCallback(() => {
    if (cooldownRemaining > 0 || loading) return;
    fetchBrief();
    setCooldownRemaining(5);
  }, [cooldownRemaining, loading, fetchBrief]);


  const getVerdictBadgeStyle = (verdict: string) => {
    switch (verdict) {
      case 'STRONG_ALIGNMENT':
        return {
          bg: colorMode === 'mainland' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)',
          color: colorMode === 'mainland' ? '#ef4444' : '#10b981',
          border: colorMode === 'mainland' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.3)',
          label: '▲ 三层多头全共振',
        };
      case 'PARTIAL_ALIGNMENT':
        return {
          bg: 'rgba(59, 130, 246, 0.15)',
          color: '#3b82f6',
          border: 'rgba(59, 130, 246, 0.3)',
          label: '◆ 双层方向一致',
        };
      case 'PARTIAL_DIVERGENCE':
        return {
          bg: 'rgba(245, 158, 11, 0.15)',
          color: '#f59e0b',
          border: 'rgba(245, 158, 11, 0.3)',
          label: '◆ 跨层背离分歧警戒',
        };
      case 'CHOPPY_EQUILIBRIUM':
        return {
          bg: 'rgba(100, 116, 139, 0.15)',
          color: '#94a3b8',
          border: 'rgba(100, 116, 139, 0.3)',
          label: '─ 盘整震荡平衡 (3层均势)',
        };
      case 'DATA_LAG_WARNING':
        return {
          bg: 'rgba(220, 38, 38, 0.15)',
          color: '#dc2626',
          border: 'rgba(220, 38, 38, 0.3)',
          label: '✕ 时钟偏斜超限警戒',
        };
      default:
        return {
          bg: 'rgba(148, 163, 184, 0.15)',
          color: '#94a3b8',
          border: 'rgba(148, 163, 184, 0.3)',
          label: verdict || '数据就绪中 (UNAVAILABLE)',
        };
    }
  };

  const getTelemetryBadge = (state: string) => {
    switch (state) {
      case 'fresh':
        return <span className="status-badge status-fresh">● 极鲜实时 FRESH</span>;
      case 'stale':
        return <span className="status-badge status-stale">▲ 延迟快照 STALE</span>;
      case 'loading':
        return <span className="status-badge status-loading">◌ 采样同步中 LOADING</span>;
      case 'disconnected':
        return <span className="status-badge status-disconnected">✕ 链路断开 DISCONNECTED</span>;
      default:
        return <span className="status-badge status-empty">◌ 空态无数据 EMPTY</span>;
    }
  };

  // Deterministic 3-Tier Categorization for Observations
  const categorizedObservations = useMemo(() => {
    if (!data?.trader_actionable_observations) {
      return { risks: [], dynamics: [], levels: [] };
    }

    const risks: string[] = [];
    const dynamics: string[] = [];
    const levels: string[] = [];

    const riskKeywords = ['偏斜', '超限', '时滞', '告警', '风险', '倒挂', 'SKEW', 'WARNING', 'RISK', 'LAG'];
    const dynamicKeywords = ['多头', '空头', '共振', '分歧', '突破', '震荡', '平衡', '动量', 'BULL', 'BEAR', 'ALIGNMENT', 'TREND'];

    data.trader_actionable_observations.forEach((obs) => {
      const isRisk = riskKeywords.some((kw) => obs.includes(kw));
      if (isRisk) {
        risks.push(obs);
        return;
      }

      const isDynamic = dynamicKeywords.some((kw) => obs.includes(kw));
      if (isDynamic) {
        dynamics.push(obs);
        return;
      }

      levels.push(obs);
    });

    return { risks, dynamics, levels };
  }, [data?.trader_actionable_observations]);

  // State 1: Disconnected / Error
  if (error || (data && data.telemetry_state === 'disconnected')) {
    return (
      <div className="view-panel active crypto-brief-container terminal-mode" id="view-crypto-brief" role="tabpanel">
        <div className="view-header brief-compact-header">
          <div className="view-title-group bilingual-view-header">
            <div className="bilingual-header-top">
              <h2 className="bilingual-header-title-zh">
                <span className="section-symbol">◈</span>
                <span>{CRYPTO_BILINGUAL.brief.zh}</span>
              </h2>
              <span className="terminal-badge">MH-QUANT-COCKPIT</span>
            </div>
            <span className="bilingual-header-subtitle-en" title="CRYPTO EXECUTIVE AI BRIEF COCKPIT">{CRYPTO_BILINGUAL.brief.en}</span>
            <p className="view-subtitle font-mono" style={{ margin: '3px 0 0 0' }}>
              全景量化决策简报 · 跨时框共振中枢
            </p>
          </div>
          <div className="view-actions">
            <button className="toggle-btn" onClick={() => fetchBrief(true)} disabled={loading} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)', padding: '4px 10px', fontSize: '12px' }}>
              {loading ? '重连中...' : '重新连接'}
            </button>
          </div>
        </div>

        <div className="brief-error-card">
          <div className="error-indicator">✕</div>
          <div className="bilingual-card-title" style={{ alignItems: 'center' }}>
            <h2 className="bilingual-card-title-zh">数据链路断开</h2>
            <span className="bilingual-card-subtitle-en">DISCONNECTED</span>
          </div>
          <p className="error-desc">
            {error || '无法与 Market-Hub 后端聚合服务建立授信连接，当前处于断开脱机状态。'}
          </p>
          <div className="error-actions">
            <button className="btn-primary" onClick={() => fetchBrief(true)}>
              重试拉取最新简报
            </button>
          </div>
        </div>
      </div>
    );
  }

  // State 2: Loading (Initial skeleton)
  if (loading && !data) {
    return (
      <div className="view-panel active crypto-brief-container terminal-mode" id="view-crypto-brief" role="tabpanel">
        <div className="view-header brief-compact-header">
          <div className="view-title-group bilingual-view-header">
            <div className="bilingual-header-top">
              <h2 className="bilingual-header-title-zh">
                <span className="section-symbol">◈</span>
                <span>{CRYPTO_BILINGUAL.brief.zh}</span>
              </h2>
              <span className="terminal-badge">MH-QUANT-COCKPIT</span>
            </div>
            <span className="bilingual-header-subtitle-en" title="CRYPTO EXECUTIVE AI BRIEF COCKPIT">{CRYPTO_BILINGUAL.brief.en}</span>
            <p className="view-subtitle font-mono" style={{ margin: '3px 0 0 0' }}>
              正在聚合宏观气象与核心 7 标的战术推演...
            </p>
          </div>
          <div className="view-actions">{getTelemetryBadge('loading')}</div>
        </div>

        <div className="brief-skeleton-grid">
          <div className="skeleton-card card-tall"></div>
          <div className="skeleton-card card-wide"></div>
          <div className="skeleton-card card-full"></div>
        </div>
      </div>
    );
  }

  // State 3: Empty
  if (!data || data.telemetry_state === 'empty') {
    return (
      <div className="view-panel active crypto-brief-container terminal-mode" id="view-crypto-brief" role="tabpanel">
        <div className="view-header brief-compact-header">
          <div className="view-title-group bilingual-view-header">
            <div className="bilingual-header-top">
              <h2 className="bilingual-header-title-zh">
                <span className="section-symbol">◈</span>
                <span>{CRYPTO_BILINGUAL.brief.zh}</span>
              </h2>
              <span className="terminal-badge">MH-QUANT-COCKPIT</span>
            </div>
            <span className="bilingual-header-subtitle-en" title="CRYPTO EXECUTIVE AI BRIEF COCKPIT">{CRYPTO_BILINGUAL.brief.en}</span>
            <p className="view-subtitle font-mono" style={{ margin: '3px 0 0 0' }}>
              核心 7 标的探针样本池暂无数据物化
            </p>
          </div>
          <div className="view-actions">{getTelemetryBadge('empty')}</div>
        </div>

        <div className="brief-empty-card">
          <div className="empty-indicator">◌</div>
          <h3>暂无可用聚合行情数据</h3>
          <p>行情采集守护进程正在冷启动或尚未推送有效价格样本。</p>
          <button className="btn-secondary" onClick={() => fetchBrief(true)}>刷新检查</button>
        </div>
      </div>
    );
  }

  const {
    macro_climate,
    core7_briefs,
    cross_layer_alignment,
    telemetry_state,
    snapshot_skew_seconds,
    macro_tactical_stance,
    top_candidate_opportunities,
    fragility_risk_radar,
    falsifiable_hypotheses,
  } = data;

  const verdictStyle = getVerdictBadgeStyle(cross_layer_alignment.alignment_verdict);

  const isBullishScore = macro_climate.sentiment_score
    ? compareDecimalStrings(macro_climate.sentiment_score, '60.00') >= 0
    : false;
  const isBearishScore = macro_climate.sentiment_score
    ? compareDecimalStrings(macro_climate.sentiment_score, '40.00') <= 0
    : false;
  const scoreProgressColor = isBullishScore
    ? colorMode === 'mainland' ? '#ef4444' : '#10b981'
    : isBearishScore
    ? colorMode === 'mainland' ? '#10b981' : '#ef4444'
    : '#3b82f6';

  const isSkewExceeded = cross_layer_alignment.skew_status === 'SKEW_EXCEEDED';

  return (
    <div className="view-panel active crypto-brief-container terminal-mode" id="view-crypto-brief" role="tabpanel">
      {/* 1. TSO 2.0 Standard Aligned Compact Header (40px) */}
      <div className="view-header brief-compact-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <h2 className="bilingual-header-title-zh">
              <span className="section-symbol">◈</span>
              <span>{CRYPTO_BILINGUAL.brief.zh}</span>
            </h2>
            <span className="terminal-badge">MH-QUANT-COCKPIT</span>
          </div>
          <span className="bilingual-header-subtitle-en" title="CRYPTO EXECUTIVE AI BRIEF COCKPIT">{CRYPTO_BILINGUAL.brief.en}</span>
          <p className="view-subtitle font-mono" style={{ margin: '3px 0 0 0' }}>
            AI 决策简报中枢 · 独立博弈对手盘 · 纯量化推演中枢 · 严格因果时延 W &gt;= 3s
          </p>
        </div>

        <div className="view-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getTelemetryBadge(telemetry_state)}
          {isSkewExceeded && (
            <span className="skew-warning-tag">▲ 时滞偏斜: {snapshot_skew_seconds}s</span>
          )}
          <button
            className="toggle-btn"
            id="btn-refresh-brief"
            onClick={handleManualRefresh}
            disabled={loading || cooldownRemaining > 0}
            title={cooldownRemaining > 0 ? `刷新冷却中 (${cooldownRemaining}s)` : '点击强制刷新'}
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)',
              padding: '4px 10px',
              fontSize: '12px',
              cursor: loading || cooldownRemaining > 0 ? 'not-allowed' : 'pointer',
              opacity: loading || cooldownRemaining > 0 ? 0.6 : 1,
            }}
          >
            {loading ? '同步中...' : cooldownRemaining > 0 ? `↻ 冷却中 (${cooldownRemaining}s)` : '↻ 实时同步'}
          </button>
        </div>
      </div>

      {/* 2. Compact Single-Line Macro Status Ribbon (32px) */}
      <div className="brief-sub-bar font-mono">
        <div className="sub-bar-item">
          <span className="sub-bar-lbl">宏观情绪:</span>
          <span className="sub-bar-val font-bold" style={{ color: scoreProgressColor }}>
            {macro_climate.sentiment_score ? `${macro_climate.sentiment_score}/100` : '--'}
          </span>
        </div>
        <div className="sub-bar-divider">|</div>
        <div className="sub-bar-item">
          <span className="sub-bar-lbl">4H 波动体制:</span>
          <span className="sub-bar-val">{macro_climate.volatility_regime ?? '--'}</span>
        </div>
        <div className="sub-bar-divider">|</div>
        <div className="sub-bar-item">
          <span className="sub-bar-lbl">宏观定调:</span>
          <span className="sub-bar-val">{macro_climate.primary_regime}</span>
        </div>
        <div className="sub-bar-divider">|</div>
        <div className="sub-bar-item">
          <span className="sub-bar-lbl">共振裁定:</span>
          <span
            className="sub-bar-pill"
            style={{
              background: verdictStyle.bg,
              color: verdictStyle.color,
              border: `1px solid ${verdictStyle.border}`,
            }}
          >
            {verdictStyle.label}
          </span>
        </div>
        <div className="sub-bar-divider">|</div>
        <div className="sub-bar-item">
          <span className="sub-bar-lbl">时滞偏斜:</span>
          <span className="sub-bar-val">{snapshot_skew_seconds}s</span>
        </div>
        <div className="sub-bar-spacer" />
        <span className="adr-cite text-muted">ADR-060 SSOT</span>
        <a href="#/weather" className="sub-bar-link" title="跳转至 View 3 趋势气象中枢求证">
          求证 View 3 气象 ↗
        </a>
      </div>

      {/* Stale Warning Banner */}
      {telemetry_state === 'stale' && (
        <div className="stale-banner">
          <span>▲ <strong>行情时效延迟预警</strong>：部分生产者样本物化时间超过新鲜度阈值，数据已降级为陈旧快照展示。</span>
        </div>
      )}

      {/* 3. Primary Viewport Above The Fold: The Sparring Cockpit (AI 战术观点与博弈对手盘) */}
      <div className="sparring-cockpit-grid">
        {/* Card 1: 首席宏观战术定调 */}
        <div className="sparring-card stance-card">
          <div className="sparring-card-header">
            <div className="sparring-header-left bilingual-card-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span className="section-symbol">◈</span>
                <h2 className="sparring-title bilingual-card-title-zh" style={{ margin: 0 }}>AI 宏观战术定调</h2>
              </div>
              <span className="bilingual-card-subtitle-en">MACRO TACTICAL STANCE</span>
            </div>
            <div className="sparring-header-right">
              <span className={`posture-badge posture-${macro_tactical_stance?.posture?.toLowerCase() || 'unknown'}`}>
                {macro_tactical_stance?.posture_label || '战术研判中'}
              </span>
              <span className="risk-level-badge font-mono">
                风险: {macro_tactical_stance?.risk_level || '--'}
              </span>
            </div>
          </div>
          <div className="sparring-body">
            <div className="stance-headline-banner">
              <h3 className="stance-headline">
                {macro_tactical_stance?.headline || '宏观定调分析计算中...'}
              </h3>
              <p className="stance-bias">
                {macro_tactical_stance?.bias_summary || '正在聚合多时框与跨层指标综合分析...'}
              </p>
            </div>
            <div className="stance-meta-grid font-mono">
              <div className="meta-tile driver-tile">
                <span className="meta-tile-label">◈ 核心驱动:</span>
                <span className="meta-tile-content">{macro_tactical_stance?.primary_driver || '--'}</span>
              </div>
              <div className="meta-tile invalidation-tile">
                <span className="meta-tile-label">▲ 失效判定:</span>
                <span className="meta-tile-content">{macro_tactical_stance?.invalidation_trigger || '--'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: 优选博弈标的透析 */}
        <div className="sparring-card candidates-card">
          <div className="sparring-card-header">
            <div className="sparring-header-left bilingual-card-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span className="section-symbol">▲</span>
                <h2 className="sparring-title bilingual-card-title-zh" style={{ margin: 0 }}>优选博弈标的透析</h2>
              </div>
              <span className="bilingual-card-subtitle-en">TOP CANDIDATE SETUPS</span>
            </div>
            <div className="sparring-header-right">
              <span className="badge-count-tag font-mono">
                {top_candidate_opportunities?.length ?? 0} 个有效候选
              </span>
            </div>
          </div>
          <div className="candidate-list">
            {top_candidate_opportunities && top_candidate_opportunities.length > 0 ? (
              top_candidate_opportunities.map((opp) => (
                <div key={opp.symbol} className="candidate-item">
                  <div className="candidate-item-top">
                    <div className="candidate-item-title-group">
                      <span className="candidate-sym font-mono font-bold">{opp.symbol}</span>
                      <span className="candidate-stance-tag">{opp.stance_label}</span>
                      {opp.data_state && (
                        <span className={`candidate-state-pill font-mono state-${opp.data_state}`}>
                          ● {opp.data_state}
                        </span>
                      )}
                    </div>
                    <div className="candidate-item-rr-group font-mono">
                      <span className="candidate-setup-type">{opp.setup_type}</span>
                      <span className="candidate-rr font-bold">
                        赔率 R:R {opp.risk_reward_ratio ?? '--'}
                      </span>
                    </div>
                  </div>

                  <div className="candidate-levels-grid font-mono">
                    <div className="level-col entry-col">
                      <span className="level-col-label">吸筹建仓带</span>
                      <span className="level-col-val">${opp.entry_zone_low ?? '--'} ~ ${opp.entry_zone_high ?? '--'}</span>
                    </div>
                    <div className="level-col inv-col">
                      <span className="level-col-label">失效防守位</span>
                      <span className="level-col-val">${opp.invalidation_level ?? '--'}</span>
                    </div>
                    <div className="level-col tgt-col">
                      <span className="level-col-label">预期目标带</span>
                      <span className="level-col-val">${opp.target_zone_low ?? '--'} ~ ${opp.target_zone_high ?? '--'}</span>
                    </div>
                  </div>

                  <div className="candidate-action-row">
                    <p className="candidate-thesis font-mono">{opp.thesis}</p>
                    <a href={opp.drill_down_route} className="drill-link font-mono" title={`跳转至 View 4 求证 ${opp.symbol}`}>
                      {opp.drill_down_label}
                    </a>
                  </div>
                </div>
              ))
            ) : (
              <div className="candidate-empty text-muted font-mono">
                {telemetry_state === 'stale'
                  ? '▲ 行情时效延迟 (STALE)：为防范假信号已暂停输出战术点位，待数据刷新'
                  : telemetry_state === 'loading'
                  ? '◌ 4H 宏观与指标特征采样聚合中...'
                  : '◌ 暂无符合严格盈亏比 (R:R > 1.0) 的优选博弈标的'}
              </div>
            )}
          </div>
        </div>

        {/* Card 3: 脆弱空气断层与尾部风险雷达 */}
        <div className="sparring-card radar-card">
          <div className="sparring-card-header">
            <div className="sparring-header-left bilingual-card-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span className="section-symbol">◈</span>
                <h2 className="sparring-title bilingual-card-title-zh" style={{ margin: 0 }}>脆弱断层与尾部风险雷达</h2>
              </div>
              <span className="bilingual-card-subtitle-en">FRAGILITY & TAIL RISK RADAR</span>
            </div>
            <a href={fragility_risk_radar?.drill_down_route || '#/feeds'} className="header-link font-mono" title="跳转至 View 5 费率与基差求证">
              {fragility_risk_radar?.drill_down_label || '求证 View 5 ↗'}
            </a>
          </div>
          <div className="radar-body">
            {[
              {
                id: 'air_pocket',
                label: '空气断层预警',
                icon: '◇',
                text: fragility_risk_radar?.air_pocket_warning || '--',
              },
              {
                id: 'squeeze',
                label: '轧空/踩踏拥挤度',
                icon: '▲',
                text: fragility_risk_radar?.squeeze_risk || '--',
              },
              {
                id: 'basis',
                label: '期现异动雷达',
                icon: '◆',
                text: fragility_risk_radar?.basis_dislocation || '--',
              },
            ].map((item) => {
              const text = item.text;
              const isUnlinked = text.includes('未接入') || text.includes('待接入');
              const isStale = text.includes('数据陈旧') || text.includes('已过时效');
              const isAlert = text.includes('预警') || text.includes('断层') || text.includes('偏离') || text.includes('超限');
              const statusClass = isUnlinked
                ? 'radar-chip-unlinked'
                : isStale
                ? 'radar-chip-stale'
                : isAlert
                ? 'radar-chip-alert'
                : 'radar-chip-normal';
              const statusLabel = isUnlinked
                ? '待接入'
                : isStale
                ? '数据陈旧'
                : isAlert
                ? '风险预警'
                : '指标正常';

              return (
                <div key={item.id} className="radar-item">
                  <div className="radar-item-top">
                    <span className="radar-label font-mono">
                      <span className="radar-icon">{item.icon}</span> {item.label}:
                    </span>
                    <span className={`radar-status-chip font-mono ${statusClass}`}>
                      ● {statusLabel}
                    </span>
                  </div>
                  <p className="radar-text">{text}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Card 4: 可证伪假设清单 */}
        <div className="sparring-card hyp-card">
          <div className="sparring-card-header">
            <div className="sparring-header-left bilingual-card-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span className="section-symbol">◈</span>
                <h2 className="sparring-title bilingual-card-title-zh" style={{ margin: 0 }}>可证伪量化假设清单</h2>
              </div>
              <span className="bilingual-card-subtitle-en">FALSIFIABLE HYPOTHESES</span>
            </div>
            <div className="sparring-header-right">
              <span className="badge-tag-amber font-mono">严禁盲信 · 必须可证伪</span>
            </div>
          </div>
          <div className="hyp-list">
            {falsifiable_hypotheses && falsifiable_hypotheses.length > 0 ? (
              falsifiable_hypotheses.map((h) => (
                <div key={h.hypothesis_id} className="hyp-item">
                  <div className="hyp-top-row">
                    <div className="hyp-title-wrap">
                      <span className="hyp-id font-mono font-bold">{h.hypothesis_id}</span>
                      <span className="hyp-statement font-medium">{h.statement}</span>
                    </div>
                    {h.confidence_score ? (
                      <span className="hyp-conf font-mono">置信度: {h.confidence_score}%</span>
                    ) : (
                      <span className="hyp-conf font-mono text-muted">置信度: 待物化</span>
                    )}
                  </div>
                  <div className="hyp-falsify-row font-mono">
                    <div className="falsify-content">
                      <span className="falsify-lbl">失效翻转:</span>
                      <span className="falsify-val">{h.falsification_condition}</span>
                    </div>
                    <a href={h.drill_down_route} className="drill-link-micro font-mono" title={`跳转求证 ${h.drill_down_label}`}>
                      {h.drill_down_label}
                    </a>
                  </div>
                </div>
              ))
            ) : (
              <div className="hyp-empty text-muted font-mono">待跨层特征物化后生成可证伪假设...</div>
            )}
          </div>
        </div>
      </div>

      {/* 4. Core-7 Cross-Sectional Matrix & Tactical Trading Reference Table */}
      <div className="brief-card core7-matrix-card">
        <div className="card-header">
          <div className="card-title-group bilingual-card-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span className="section-symbol">◈</span>
              <h2 className="card-title bilingual-card-title-zh">{CARD_TITLES_BILINGUAL.core7Matrix.zh}</h2>
            </div>
            <span className="bilingual-card-subtitle-en">{CARD_TITLES_BILINGUAL.core7Matrix.en}</span>
          </div>
          <div className="card-tags-group">
            <span className="fib-disclaimer font-mono text-muted">
              * 仅供客观数学结构参考，严禁作为买卖/止损/交易指令依据
            </span>
          </div>
        </div>

        <div className="core7-table-container">
          <table className="core7-terminal-table">
            <thead>
              <tr>
                <th className="th-symbol">标的资产</th>
                <th className="th-price">最新现货价</th>
                <th className="th-chg">24h 涨跌</th>
                <th className="th-basis">基差 (bps)</th>
                <th className="th-fib">4H 斐波那契标尺 (0.382 支撑 ─●─ 0.618 阻力)</th>
                <th className="th-bias">多空偏好</th>
                <th className="th-zone">建议博弈区间</th>
                <th className="th-rr">赔率 R:R</th>
                <th className="th-regime">量价体制</th>
                <th className="th-state">数据状态</th>
                <th className="th-actions">快捷求证</th>
              </tr>
            </thead>
            <tbody>
              {core7_briefs.map((b: ExtendedCore7BriefItem) => {
                const sign = getDecimalSign(b.change_24h_pct);
                const isUp = sign === 1;
                const isDown = sign === -1;
                const chgColor = !b.change_24h_pct
                  ? 'var(--text-muted)'
                  : isUp
                  ? colorMode === 'mainland' ? '#ef4444' : '#10b981'
                  : isDown
                  ? colorMode === 'mainland' ? '#10b981' : '#ef4444'
                  : 'var(--text-muted)';

                const fibPos = getPriceToFibPercent(
                  b.spot_price,
                  b.observed_fibonacci_levels?.fib_support_382,
                  b.observed_fibonacci_levels?.fib_resistance_618
                );

                const supp382Formatted = b.observed_fibonacci_levels?.fib_support_382
                  ? formatFinancialPrice(b.observed_fibonacci_levels.fib_support_382, b.symbol)
                  : '--';
                const res618Formatted = b.observed_fibonacci_levels?.fib_resistance_618
                  ? formatFinancialPrice(b.observed_fibonacci_levels.fib_resistance_618, b.symbol)
                  : '--';

                return (
                  <tr key={b.symbol} className="core7-tr">
                    <td className="td-symbol">
                      <CryptoSymbolCell symbol={b.symbol} />
                    </td>
                    <td className="td-price font-mono font-bold">
                      {b.spot_price ? `$${formatFinancialPrice(b.spot_price, b.symbol)}` : '--'}
                    </td>
                    <td className="td-chg font-mono font-bold" style={{ color: chgColor }}>
                      {b.change_24h_pct ? (isUp ? `+${b.change_24h_pct}%` : `${b.change_24h_pct}%`) : '--'}
                    </td>
                    <td className="td-basis font-mono">
                      {b.basis_bps ? `${b.basis_bps} bps` : '--'}
                    </td>
                    <td className="td-fib">
                      {fibPos !== null ? (
                        <div className="fib-range-bar-wrapper">
                          <span className="fib-bound-lbl font-mono">{supp382Formatted}</span>
                          <div className="fib-track-bar" title={`相对 4H 斐波那契区间位置: ${fibPos}%`}>
                            <div className="fib-track-fill" style={{ width: `${fibPos}%` }} />
                            <div className="fib-track-pin" style={{ left: `${fibPos}%` }} />
                          </div>
                          <span className="fib-bound-lbl font-mono">{res618Formatted}</span>
                        </div>
                      ) : (
                        <span className="fib-unavailable font-mono text-muted">── 待计算物化 ──</span>
                      )}
                    </td>
                    <td className="td-bias font-mono">
                      {b.tactical_bias ? (
                        <span className="bias-badge">{b.tactical_bias}</span>
                      ) : (
                        <span className="text-muted">待物化</span>
                      )}
                    </td>
                    <td className="td-zone font-mono">
                      {b.suggested_action_zone ? (
                        <span className="zone-pill font-mono">{b.suggested_action_zone}</span>
                      ) : (
                        <span className="text-muted">待物化</span>
                      )}
                    </td>
                    <td className="td-rr font-mono font-bold">
                      {b.risk_reward_ratio ? `R:R ${b.risk_reward_ratio}` : '--'}
                    </td>
                    <td className="td-regime font-mono">
                      {b.volume_profile_regime ? (
                        <span className="regime-tag">{b.volume_profile_regime}</span>
                      ) : (
                        <span className="regime-unavailable text-muted">--</span>
                      )}
                    </td>
                    <td className="td-state font-mono">
                      <span className="state-badge-pill">{b.data_state}</span>
                    </td>
                    <td className="td-actions">
                      <div className="action-links-row">
                        <a
                          href={`#/indicators?symbol=${b.symbol}`}
                          className="action-btn-link"
                          title="跳转至 View 4 多周期指标视图求证"
                        >
                          指标
                        </a>
                        <a href="#/weather" className="action-btn-link secondary" title="跳转至 View 3 趋势气象中枢求证">
                          气象
                        </a>
                        <a href="#/crypto/regime-decision" className="action-btn-link secondary" title="跳转至 View 7 态势回测求证">
                          回测
                        </a>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 5. Objective Trader Observations (3-Tier Structured Layout) */}
      {data.trader_actionable_observations && data.trader_actionable_observations.length > 0 && (
        <div className="brief-card observations-card">
          <div className="card-header">
            <div className="card-title-group bilingual-card-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span className="section-symbol">◈</span>
                <h2 className="card-title bilingual-card-title-zh">{CARD_TITLES_BILINGUAL.evidenceObservations.zh}</h2>
              </div>
              <span className="bilingual-card-subtitle-en">{CARD_TITLES_BILINGUAL.evidenceObservations.en}</span>
            </div>
            <span className="card-tag font-mono">零模拟数据 · 真实物化快照</span>
          </div>

          <div className="observations-tiered-grid">
            {/* Tier 1: Risks & Alerts */}
            {categorizedObservations.risks.length > 0 && (
              <div className="obs-tier-col tier-risk">
                <div className="obs-tier-header">
                  <span className="tier-tag">尾部风险与异动警报</span>
                  <span className="tier-count font-mono">{categorizedObservations.risks.length}</span>
                </div>
                <ul className="obs-tier-list">
                  {categorizedObservations.risks.map((obs, idx) => (
                    <li key={idx} className="obs-item risk-item">
                      <span className="obs-marker">▲</span>
                      <span className="obs-content">{obs}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tier 2: Regime Dynamics & Trend */}
            {categorizedObservations.dynamics.length > 0 && (
              <div className="obs-tier-col tier-dynamic">
                <div className="obs-tier-header">
                  <span className="tier-tag">结构与动量演化</span>
                  <span className="tier-count font-mono">{categorizedObservations.dynamics.length}</span>
                </div>
                <ul className="obs-tier-list">
                  {categorizedObservations.dynamics.map((obs, idx) => (
                    <li key={idx} className="obs-item dynamic-item">
                      <span className="obs-marker">◆</span>
                      <span className="obs-content">{obs}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tier 3: Key Levels & Geometry */}
            {categorizedObservations.levels.length > 0 && (
              <div className="obs-tier-col tier-level">
                <div className="obs-tier-header">
                  <span className="tier-tag">关键位置与客观观察</span>
                  <span className="tier-count font-mono">{categorizedObservations.levels.length}</span>
                </div>
                <ul className="obs-tier-list">
                  {categorizedObservations.levels.map((obs, idx) => (
                    <li key={idx} className="obs-item level-item">
                      <span className="obs-marker">●</span>
                      <span className="obs-content">{obs}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 6. Top-Down Progressive Verification Workflow Navigator */}
      <div className="brief-card pipeline-navigator-card">
        <div className="card-header">
          <div className="card-title-group bilingual-card-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span className="section-symbol">◈</span>
              <h2 className="card-title bilingual-card-title-zh">{CARD_TITLES_BILINGUAL.topDownWorkflow.zh}</h2>
            </div>
            <span className="bilingual-card-subtitle-en">{CARD_TITLES_BILINGUAL.topDownWorkflow.en}</span>
          </div>
          <span className="card-tag font-mono">从顶层推演直达底层量化求证</span>
        </div>

        <div className="pipeline-steps-grid">
          <div className="step-card current">
            <div className="step-num font-mono">1</div>
            <div className="step-body">
              <span className="step-name">AI 决策简报</span>
              <span className="step-desc">博弈定调与战术推演 (当前所在)</span>
            </div>
            <span className="step-badge font-mono">ACTIVE</span>
          </div>

          <a href="#/crypto/market-board" className="step-card next">
            <div className="step-num font-mono">2</div>
            <div className="step-body">
              <span className="step-name">多资产市场中枢</span>
              <span className="step-desc">全市场广度与 MTC 多时框共识</span>
            </div>
            <span className="step-arrow">→</span>
          </a>

          <a href="#/weather" className="step-card">
            <div className="step-num font-mono">3</div>
            <div className="step-body">
              <span className="step-name">趋势气象中枢</span>
              <span className="step-desc">7 时框趋势形态与天气晴雨投影</span>
            </div>
            <span className="step-arrow">→</span>
          </a>

          <a href="#/indicators" className="step-card">
            <div className="step-num font-mono">4</div>
            <div className="step-body">
              <span className="step-name">多周期指标流中枢</span>
              <span className="step-desc">RSI / MACD / 布林带客观因子</span>
            </div>
            <span className="step-arrow">→</span>
          </a>

          <a href="#/feeds" className="step-card">
            <div className="step-num font-mono">5</div>
            <div className="step-body">
              <span className="step-name">资产流与量化雷达</span>
              <span className="step-desc">现货/合约基差与底层报价流</span>
            </div>
            <span className="step-arrow">→</span>
          </a>

          <a href="#/crypto/regime-decision" className="step-card">
            <div className="step-num font-mono">7</div>
            <div className="step-body">
              <span className="step-name">态势决策与回测</span>
              <span className="step-desc">筹码密集带与历史胜率回测</span>
            </div>
            <span className="step-arrow">→</span>
          </a>
        </div>
      </div>
    </div>
  );
};
